import { z } from "zod";
import { REFERRAL_STATUSES } from "@/features/recruitment/referral-vocabulary";

/**
 * URL search params for `/$workspaceSlug/recruitment/referrals`.
 *
 * Both live in the URL rather than in `useState` (S3): "the referrals I am
 * still waiting on" and "every open referral in the workspace" are both links
 * somebody sends, and the back button should undo a filter rather than leave
 * the page.
 */
export const referralsRouteSearchSchema = z
  .object({
    scope: z.enum(["own", "workspace"]).optional(),
    status: z.enum(REFERRAL_STATUSES).optional(),
  })
  .strict();

export type ReferralsRouteSearch = z.infer<typeof referralsRouteSearchSchema>;
export type ReferralScope = NonNullable<ReferralsRouteSearch["scope"]>;

/**
 * Which lens the URL means, for a given viewer.
 *
 * `own` is the default for everyone, admins included: an admin arriving from
 * a notification about their own referral should land on their own rows, and
 * the workspace lens is one click away. A `?scope=workspace` from a shared
 * admin link resolves back to `own` for a member rather than rendering a tab
 * the shore would 403, which is the same normalisation the recruitment
 * planning route does for its tabs.
 */
export function resolveReferralScope(
  search: ReferralsRouteSearch,
  canManageWorkspace: boolean,
): ReferralScope {
  if (!canManageWorkspace) return "own";
  return search.scope ?? "own";
}
